import { FC, useEffect, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { useUpdateNote } from '@/services/noteGoApi/noteGoApiComponents';
import { queryKeyFn } from '@/services/noteGoApi/noteGoApiContext';
import useAlertStore from '@/stores/alertStore';

type SummaryFeedbackValue = 0 | 1 | null;

interface SummaryFeedbackProps {
  noteId: string;
  value: SummaryFeedbackValue;
  onChange?: (value: SummaryFeedbackValue) => void;
}

const SummaryFeedback: FC<SummaryFeedbackProps> = ({ noteId, value, onChange }) => {
  const { userToken } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);
  const queryClient = useQueryClient();

  const [currentValue, setCurrentValue] = useState<SummaryFeedbackValue>(value ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setCurrentValue(value ?? null);
  }, [value]);

  const { mutateAsync: updateNote } = useUpdateNote({
    onSuccess: (_data, variables) => {
      // Invalidate the note query cache to ensure fresh data on reload
      const queryKey = queryKeyFn({
        path: '/notes/{noteId}',
        operationId: 'getNote',
        variables: {
          pathParams: { noteId },
          headers: { authorization: `Bearer ${userToken}` },
        },
      });
      queryClient.invalidateQueries({ queryKey });

      // Notify parent component of the change
      const feedback = variables.body?.summary_feedback;
      onChange?.(feedback === 0 || feedback === 1 ? feedback : null);

      // Show success message to user
      showAlert('Thank you for your feedback!', 'success');
      setIsSubmitting(false);
    },
    onError: () => {
      showAlert('Something went wrong while saving your feedback. Try again later.', 'error');
      setIsSubmitting(false);
    },
  });

  const handleFeedbackChange = async (nextValue: 0 | 1) => {
    if (!noteId || isSubmitting) {
      return;
    }

    const toggledValue = currentValue === nextValue ? null : nextValue;
    const previousValue = currentValue;

    // Optimistically update the UI
    setCurrentValue(toggledValue);
    setIsSubmitting(true);

    try {
      await updateNote({
        headers: { authorization: `Bearer ${userToken}` },
        pathParams: { noteId },
        body: {
          summary_feedback: toggledValue,
        },
      });
    } catch (error) {
      // Revert on error and notify user
      setCurrentValue(previousValue);
      setIsSubmitting(false);
      showAlert('Something went wrong while saving your feedback. Try again later.', 'error');
    }
  };

  return (
    <Box
      sx={{
        width: 'fit-content',
        margin: '0 auto',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '8px',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        padding: '8px',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          This result
        </Typography>
        <Tooltip title="Thumbs up">
          <span>
            <IconButton
              aria-label="Thumbs up on this result"
              size="small"
              color={currentValue === 1 ? 'primary' : 'default'}
              onClick={() => handleFeedbackChange(1)}
              disabled={isSubmitting}
            >
              {currentValue === 1 ? <ThumbUpAltIcon fontSize="small" /> : <ThumbUpOffAltIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Thumbs down">
          <span>
            <IconButton
              aria-label="Thumbs down on this result"
              size="small"
              color={currentValue === 0 ? 'primary' : 'default'}
              onClick={() => handleFeedbackChange(0)}
              disabled={isSubmitting}
            >
              {currentValue === 0 ? <ThumbDownAltIcon fontSize="small" /> : <ThumbDownOffAltIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export type { SummaryFeedbackValue };
export default SummaryFeedback;
